/**
 * Écoute d'un passage de l'enregistrement, partagée par les composants qui
 * portent une balise :
 *   <audio ref="player" :src="recordingUrl" preload="metadata" @timeupdate="onTimeUpdate" @pause="onPause" @ended="onPause">
 * `playRange(id, start, end)` lit de `start` à `end` (secondes de la
 * transcription) ; `playingId` dit ce qui joue, `stopPlaying()` arrête.
 * Le composant fournit `meeting` (pour la durée mesurée) et `recordingUrl`.
 */

/** Marge jouée avant et après le passage, pour entendre l'attaque de la phrase. */
export const PLAY_MARGIN_SECONDS = 0.8

export default {
  data () {
    return {
      playingId: null,
      stopAt: null
    }
  },
  watch: {
    'meeting.uuid' () {
      this.stopPlaying()
    }
  },
  beforeDestroy () {
    this.stopPlaying()
  },
  methods: {
    /**
     * Facteur entre l'échelle de temps du navigateur et celle des segments. Sur
     * un mp3 à débit variable sans en-tête de durée, le navigateur estime la
     * durée d'après le premier bloc et se positionne en proportion du fichier :
     * un segment à 3 min 10 s réel se trouve alors à 3 min 10 s × (durée estimée
     * / durée réelle). La durée réelle est celle mesurée par le fournisseur de
     * transcription sur l'audio décodé.
     */
    timeScale (player) {
      const transcription = this.meeting.transcription && typeof this.meeting.transcription === 'object' ? this.meeting.transcription : {}
      const known = Number(transcription.duration_seconds)
      const estimated = Number(player.duration)
      if (!known || !Number.isFinite(estimated) || estimated <= 0) {
        return 1
      }
      const scale = estimated / known
      return Math.abs(scale - 1) > 0.02 ? scale : 1
    },
    playRange (id, start, end) {
      const player = this.$refs.player
      if (!player) {
        return
      }
      const scale = this.timeScale(player)
      const from = Math.max(0, Number(start) || 0) * scale
      this.stopAt = Math.max(Number(end) || 0, from + 1) * scale
      this.playingId = id
      const begin = () => {
        try {
          player.currentTime = from
        } catch (e) {
          // Métadonnées pas encore lues : la lecture part du début du fichier.
        }
        const playing = player.play()
        if (playing && playing.catch) {
          playing.catch(() => {
            this.playingId = null
            this.stopAt = null
            this.$buefy.notification.open({
              duration: 4000,
              message: 'Impossible de lire l’enregistrement.',
              position: 'is-bottom-right',
              type: 'is-danger',
              hasIcon: false
            })
          })
        }
      }
      if (player.readyState >= 1) {
        begin()
      } else {
        player.addEventListener('loadedmetadata', begin, { once: true })
        player.load()
      }
    },
    onTimeUpdate () {
      const player = this.$refs.player
      if (player && this.stopAt !== null && player.currentTime >= this.stopAt) {
        player.pause()
      }
    },
    onPause () {
      this.playingId = null
      this.stopAt = null
    },
    stopPlaying () {
      const player = this.$refs.player
      if (player && !player.paused) {
        player.pause()
      }
      this.playingId = null
      this.stopAt = null
    }
  }
}
